import {
  GridItem,
  LayoutGrid,
  LeftArrowIcon,
  ProgressCircleV3,
  RightArrowIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type AccoladeSlide } from '@/app/[locale]/membership/_data/membershipAccolades';
import Image from '@/app/components/Image';

const MembershipAccolades = ({
  accoladeSlideData,
}: {
  readonly accoladeSlideData: AccoladeSlide[];
}): JSX.Element => {
  const [currentAccolade, setCurrentAccolade] = useState(0);
  const t = useTranslations();
  const accoladeSlidesLength = accoladeSlideData.length;
  const isFirst = currentAccolade === 0;
  const isLast = currentAccolade === accoladeSlidesLength - 1;
  //This is set to 170 at first so while the function is executing the re-render is not as obvious
  const [accoladeContainerHeight, setAccoladeContainerHeight] = useState(170);
  const slideTextRef = useRef<HTMLDivElement | null>(null);
  const slideSourceRef = useRef<HTMLDivElement | null>(null);
  const slideLogoRef = useRef<HTMLImageElement | null>(null);

  //This function finds the tallest accolade and then sets the height of the container with 32 px on top and bottom to ensure the card buttons below the container dont jump as you click through them
  useEffect(() => {
    if (slideTextRef.current != null && slideLogoRef.current != null) {
      const componentHeight =
        slideTextRef.current.clientHeight + slideLogoRef.current.clientHeight;
      setAccoladeContainerHeight(
        Math.max(componentHeight + 64, accoladeContainerHeight),
      );
    } else if (slideTextRef.current != null && slideSourceRef.current != null) {
      const componentHeight =
        slideTextRef.current.clientHeight + slideSourceRef.current.clientHeight;
      setAccoladeContainerHeight(
        Math.max(componentHeight + 64, accoladeContainerHeight),
      );
    }
  }, [slideTextRef, slideSourceRef, slideLogoRef, accoladeContainerHeight]);

  const handlePreviousClick = useCallback(() => {
    setCurrentAccolade(() => Math.max(currentAccolade - 1, 0));
  }, [currentAccolade]);

  const handleNextClick = useCallback(() => {
    setCurrentAccolade(() =>
      Math.min(accoladeSlidesLength - 1, currentAccolade + 1),
    );
  }, [currentAccolade, accoladeSlidesLength]);

  const currentSlide: AccoladeSlide | undefined =
    accoladeSlideData[currentAccolade];
  return (
    <section
      className="relative pt-5 pb-24 md:px-5 lg:pt-24"
      data-cy="membership-accolades"
    >
      <LayoutGrid className="h-full content-between">
        <GridItem
          colStart={{ sm: 'main', md: 3, lg: 6 }}
          colEnd={{ sm: 'main', md: 23, lg: 19 }}
        >
          <TypographyV3
            Element="h2"
            variant="h3"
            className="text-xl md:text-4xl"
            height="tight"
          >
            {t('membership_accolades_title')}
          </TypographyV3>
          <div
            className="flex flex-col justify-center pl-8 md:pl-8"
            style={{
              height: `${accoladeContainerHeight}px`,
            }}
            data-cy="accolade-container"
          >
            {currentSlide && (
              <div className="flex max-w-4xl flex-col pb-8" ref={slideTextRef}>
                <TypographyV3 weight="bold">{currentSlide.year}</TypographyV3>
                <TypographyV3 variant="h6" height="relaxed">
                  {t(currentSlide.text)}
                </TypographyV3>
              </div>
            )}
            {currentSlide?.source && (
              <div ref={slideSourceRef}>
                <TypographyV3 className="m-0" data-cy="accolade-source">
                  — {currentSlide.source.name}
                  {currentSlide.source.role && (
                    <>
                      , <em>{t(currentSlide.source.role)}</em>
                    </>
                  )}
                </TypographyV3>
              </div>
            )}
            {currentSlide?.image && (
              <Image
                data-cy="accolade-image"
                src={currentSlide.image.path}
                format={currentSlide.image.format}
                alt={currentSlide.image.alt || ''}
                width="170"
                height="33"
                ref={slideLogoRef}
              />
            )}
          </div>
          <div className="flex items-center">
            <div className="flex justify-around">
              <TypographyV3
                Element="span"
                color="inherit"
                // this off sets the pading for 1/#OfSlide 7/#OfSlide as the width of these numbers is skinnier
                className={cx({
                  'pl-[2.5px]': currentAccolade == 0,
                  'pl-[2.2px]': currentAccolade == 6,
                })}
                data-cy="#slide/#OfSlides"
              >
                {`${currentAccolade + 1}/${accoladeSlidesLength}`}
              </TypographyV3>
              <ProgressCircleV3
                className="mr-4 ml-2"
                current={currentAccolade}
                total={accoladeSlidesLength - 1}
              />
            </div>
            <div className="flex gap-x-4">
              <button
                aria-label={t('previous_slide_aria_label')}
                className={cx('flex size-10 items-center justify-center p-0', {
                  'cursor-not-allowed': isFirst,
                  'opacity-30': isFirst,
                })}
                data-cy="membership_accolades_carousel_prev"
                disabled={isFirst}
                onClick={handlePreviousClick}
                type="button"
              >
                <LeftArrowIcon />
              </button>
              <button
                aria-label={t('next_slide_aria_label')}
                className={cx('flex size-10 items-center justify-center p-0', {
                  'cursor-not-allowed': isLast,
                  'opacity-30': isLast,
                })}
                data-cy="membership_accolades_carousel_next"
                disabled={isLast}
                onClick={handleNextClick}
                type="button"
              >
                <RightArrowIcon />
              </button>
            </div>
          </div>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default MembershipAccolades;
